// Hook para gestionar espacios y disponibilidad desde el panel administrativo
import { useQuery } from '@tanstack/react-query'
import { ApiClient } from '../services/api'
import type { EspacioDisponibleRango, FiltrosDisponibilidad } from '@/types/availability'

// Query keys
export const QUERY_KEYS = {
  ESPACIOS_ADMIN: 'espacios-admin',
  DISPONIBILIDAD_RANGO_ADMIN: 'disponibilidad-rango-admin',
} as const

/**
 * Hook para obtener disponibilidad de rango de fechas (Admin)
 * Endpoint: GET /api/espacios/rev_ocupacion_panoramica?include_no_reservables=true
 * Usa el endpoint unificado con parámetro include_no_reservables
 * Diferencias vs público:
 * - Requiere autenticación UAU
 * - Filtra por empresa del usuario admin (via header X-Empresa-ID)
 * - include_no_reservables=true: Muestra espacios reservables Y no-reservables
 * Uso: OcupacionPanoramicaPage - vista panorámica del camping
 */
export const useDisponibilidadRangoAdmin = (filtros: FiltrosDisponibilidad | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.DISPONIBILIDAD_RANGO_ADMIN, filtros],
    queryFn: async () => {
      const params = new URLSearchParams()

      if (filtros?.fecha_entrada) {
        params.append('fecha_entrada', filtros.fecha_entrada)
      }
      if (filtros?.fecha_salida) {
        params.append('fecha_salida', filtros.fecha_salida)
      }
      if (filtros?.tipo_espacio_id) {
        params.append('tipo_espacio_id', filtros.tipo_espacio_id)
      }

      // ✅ UNIFICADO: Usar endpoint público con include_no_reservables=true
      params.append('include_no_reservables', 'true')

      const response = await ApiClient.get<EspacioDisponibleRango[]>(
        `/api/espacios/rev_ocupacion_panoramica?${params.toString()}`
      )

      // ✅ ADMIN: Retornar TODOS los espacios (incluye no-reservables y privados)
      return response
    },
    enabled: !!(filtros && filtros.fecha_entrada && filtros.fecha_salida),
    staleTime: 2 * 60 * 1000, // 2 minutos (admin necesita datos más frescos)
    refetchOnWindowFocus: true, // Re-validar cuando vuelve a la pestaña
  })
}
